import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFitStore } from '../../store/useFitStore';
import type { GlobalExercise } from '../../store/types';

/* ============================================================
   ExercisePicker — searchable autocomplete over the shared
   exercise library (globalExercises), with a free-text fallback.

   The coach "reaches for" a library exercise while logging: type a
   few letters, pick from the list (prefilling its default sets/reps),
   or just keep typing a name the library doesn't have yet. Used by
   the SessionEditor / live logger in place of a plain name input.

   The suggestion list renders in a body-level portal with fixed
   positioning so it is never clipped by the editor's scroll container
   (the exercise rows live inside an overflow-y-auto panel).
============================================================ */

interface Props {
  value: string;
  onChange: (name: string) => void;          // free typing / cleared selection
  onPick?: (ex: GlobalExercise) => void;      // chose a library exercise (caller may prefill sets)
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
}

// Rank: exact prefix match first, then word-boundary, then any substring — each
// alphabetical within its tier. Keeps "press" → "Press" above "Leg press".
const rank = (name: string, q: string): number => {
  const n = name.toLowerCase();
  if (n.startsWith(q)) return 0;
  if (n.includes(` ${q}`)) return 1;
  if (n.includes(q)) return 2;
  return 3;
};

const ExercisePicker: React.FC<Props> = ({ value, onChange, onPick, placeholder, className, autoFocus }) => {
  const { globalExercises } = useFitStore();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0); // highlighted suggestion index
  const [rect, setRect] = useState<{ left: number; top: number; width: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimer = useRef<number | undefined>(undefined);

  const matches = useMemo(() => {
    const q = value.trim().toLowerCase();
    if (!q) return [];
    return globalExercises
      .filter((ex) => ex.name.toLowerCase().includes(q))
      // Within a match tier, shorter names first: common movements ("Bb bench
      // press") float above variant-heavy ones ("Banded bench press (Black)").
      .sort(
        (a, b) =>
          rank(a.name, q) - rank(b.name, q) ||
          a.name.length - b.name.length ||
          a.name.localeCompare(b.name)
      )
      .slice(0, 8);
  }, [globalExercises, value]);

  // An exact (case-insensitive) name match means the coach already has what they
  // typed — no need to keep nagging them with a one-item dropdown of themselves.
  const exactMatch = matches.length === 1 && matches[0].name.toLowerCase() === value.trim().toLowerCase();
  const showList = open && matches.length > 0 && !exactMatch;

  // Anchor the fixed-position list under the input, and keep it pinned while the
  // editor's inner panel (or window) scrolls/resizes. Capture-phase scroll catches
  // the overflow-y-auto ancestor, not just the window.
  useLayoutEffect(() => {
    if (!showList) return;
    const place = () => {
      const r = inputRef.current?.getBoundingClientRect();
      if (r) setRect({ left: r.left, top: r.bottom, width: r.width });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [showList]);

  useEffect(() => () => window.clearTimeout(blurTimer.current), []);

  const choose = (ex: GlobalExercise) => {
    onChange(ex.name);
    onPick?.(ex);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!showList) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') {
      // Only hijack Enter when a suggestion is highlighted; otherwise let the
      // free-typed name stand (and any parent form behaviour proceed).
      if (matches[active]) { e.preventDefault(); choose(matches[active]); }
    } else if (e.key === 'Escape') { setOpen(false); }
  };

  // Open downward by default; flip above the input when there isn't room below.
  const dropUp = rect != null && rect.top + 280 > window.innerHeight && rect.top > window.innerHeight / 2;

  return (
    <div className="relative flex-1 min-w-0">
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setActive(0); }}
        onFocus={() => setOpen(true)}
        onBlur={() => { blurTimer.current = window.setTimeout(() => setOpen(false), 120); }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className={className}
        role="combobox"
        aria-expanded={showList}
        aria-autocomplete="list"
      />
      {showList && rect && createPortal(
        <ul
          style={{
            position: 'fixed',
            left: rect.left,
            width: rect.width,
            ...(dropUp ? { bottom: window.innerHeight - rect.top + 18 } : { top: rect.top + 4 }),
            zIndex: 9999,
          }}
          className="max-h-64 overflow-y-auto no-scrollbar rounded-fit-md border border-fit-line-strong bg-fit-bg-elevated shadow-xl"
          // keep focus on the input while clicking a row (blur would close first)
          onMouseDown={(e) => { e.preventDefault(); window.clearTimeout(blurTimer.current); }}
        >
          {matches.map((ex, i) => (
            <li key={ex.id}>
              <button
                type="button"
                onClick={() => choose(ex)}
                onMouseEnter={() => setActive(i)}
                className={`w-full text-left px-3 py-2 flex items-center justify-between gap-3 ${
                  i === active ? 'bg-white/10' : 'hover:bg-white/5'
                }`}
              >
                <span className="text-[13px] font-semibold truncate">{ex.name}</span>
                <span className="text-[11px] text-fit-muted shrink-0 tabular-nums">
                  {[ex.muscleGroup, ex.defaultSets && ex.defaultReps ? `${ex.defaultSets}×${ex.defaultReps}` : '']
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </button>
            </li>
          ))}
        </ul>,
        document.body
      )}
    </div>
  );
};

export default ExercisePicker;
